import * as THREE from 'three';
import { fbm2 } from '../../core/rng';
import type { BuiltLocation, LocationDef } from '../Location';
import { paintableIceMaterial, paintableSnowMaterial, registerPaintable } from '../paintable';
import { box, collider, disposeScene, skyDome, stdMat, sun } from '../props';
import { createTerrain } from '../terrain';

const POND = { x: 18, z: -14, r: 11 };

/** A quiet farm on an overcast morning: barn, silo, fences, hay bales, and a
 * frozen pond whose glossy ice takes paint crisper than snow does. */
export const farmLocation: LocationDef = {
  id: 'farm',
  name: 'Winter Farm',
  description: 'Barn red against white fields — and a frozen pond that paints like glass.',
  thumbnail: { skyTop: '#b9c6d4', skyBottom: '#e9edf2', ground: '#f4f7fc', accent: '#a63c33' },

  build(ctx): BuiltLocation {
    const scene = new THREE.Scene();
    const colliders: THREE.Box3[] = [];
    const paintables: BuiltLocation['paintables'] = [];

    scene.fog = new THREE.FogExp2(0xd9e0e8, 0.0052);
    scene.add(
      skyDome([
        [0, '#9fb0c2'],
        [0.6, '#ccd6e0'],
        [1, '#edf1f5'],
      ]),
    );
    scene.add(new THREE.HemisphereLight(0xcdd8e4, 0xe2e6ea, 1.0));
    scene.add(sun(0xfff0d8, 0.85, -30, 60, 40, 80));

    const pondDist = (x: number, z: number) => Math.hypot(x - POND.x, z - POND.z);
    const height = (x: number, z: number) => {
      const rolling = fbm2(x * 0.03, z * 0.03, ctx.seed) * 0.9;
      // Flatten to ice level inside the pond.
      const inPond = 1 - THREE.MathUtils.smoothstep(pondDist(x, z), POND.r - 1.5, POND.r + 2.5);
      return THREE.MathUtils.lerp(rolling, -0.18, inPond);
    };

    const snow = paintableSnowMaterial({ grainScale: 1300 });
    const terrain = createTerrain({ size: 150, segments: 110, height, material: snow.material });
    scene.add(terrain.mesh);
    registerPaintable(paintables, 'ground', terrain.mesh, snow.uniform, true);

    // The frozen pond — its own paintable ice surface.
    const ice = paintableIceMaterial();
    const pond = new THREE.Mesh(new THREE.CircleGeometry(POND.r, 42), ice.material);
    pond.geometry.rotateX(-Math.PI / 2);
    pond.position.set(POND.x, -0.13, POND.z);
    pond.receiveShadow = true;
    scene.add(pond);
    registerPaintable(paintables, 'pond', pond, ice.uniform, false);

    // Barn.
    const barn = new THREE.Group();
    const red = stdMat(0xa63c33, 0.9);
    const trim = stdMat(0xf2f0ea, 0.9);
    barn.add(box(9, 5.5, 12, red, 0, 2.75, 0));
    const roofMat = stdMat(0xe9eef6, 0.95);
    const roofL = box(5.6, 0.3, 13, roofMat, -2.35, 6.4, 0);
    roofL.rotation.z = 0.55;
    const roofR = box(5.6, 0.3, 13, roofMat, 2.35, 6.4, 0);
    roofR.rotation.z = -0.55;
    barn.add(roofL, roofR);
    barn.add(box(3, 3.4, 0.2, trim, 0, 1.7, 6.05));
    barn.add(box(0.25, 3.4, 0.2, stdMat(0x5c2a24, 0.9), 0, 1.7, 6.12));
    const barnX = -20;
    const barnZ = 8;
    barn.position.set(barnX, height(barnX, barnZ), barnZ);
    barn.rotation.y = 0.35;
    scene.add(barn);
    colliders.push(
      collider(barnX, barnZ, 12, 13.5, height(barnX, barnZ), height(barnX, barnZ) + 7),
    );

    // Silo.
    const siloX = -29;
    const siloZ = -2;
    const silo = new THREE.Mesh(
      new THREE.CylinderGeometry(2.2, 2.2, 9, 18),
      stdMat(0xb9bfc7, 0.55, 0.5),
    );
    silo.position.set(siloX, height(siloX, siloZ) + 4.5, siloZ);
    silo.castShadow = true;
    scene.add(silo);
    const siloCap = new THREE.Mesh(
      new THREE.SphereGeometry(2.2, 18, 10, 0, Math.PI * 2, 0, Math.PI / 2),
      stdMat(0x8e959e, 0.5, 0.5),
    );
    siloCap.position.set(siloX, height(siloX, siloZ) + 9, siloZ);
    scene.add(siloCap);
    colliders.push(collider(siloX, siloZ, 4.6, 4.6, 0, height(siloX, siloZ) + 10));

    // Fence runs.
    const fenceMat = stdMat(0x7a6a55, 0.9);
    const fenceRun = (x0: number, z0: number, x1: number, z1: number) => {
      const len = Math.hypot(x1 - x0, z1 - z0);
      const posts = Math.floor(len / 2.4);
      const angle = Math.atan2(x1 - x0, z1 - z0);
      for (let i = 0; i <= posts; i++) {
        const t = i / posts;
        const x = THREE.MathUtils.lerp(x0, x1, t);
        const z = THREE.MathUtils.lerp(z0, z1, t);
        const y = height(x, z);
        const post = box(0.14, 1.2, 0.14, fenceMat, x, y + 0.6, z);
        scene.add(post);
        if (i < posts) {
          const nx = THREE.MathUtils.lerp(x0, x1, (i + 1) / posts);
          const nz = THREE.MathUtils.lerp(z0, z1, (i + 1) / posts);
          const midx = (x + nx) / 2;
          const midz = (z + nz) / 2;
          const midy = (y + height(nx, nz)) / 2;
          for (const railY of [0.45, 0.95]) {
            const rail = box(0.08, 0.1, 2.45, fenceMat, midx, midy + railY, midz, angle);
            scene.add(rail);
          }
        }
      }
      // One long thin collider per run.
      const cx = (x0 + x1) / 2;
      const cz = (z0 + z1) / 2;
      const w = Math.abs(x1 - x0) + 0.4;
      const d = Math.abs(z1 - z0) + 0.4;
      colliders.push(collider(cx, cz, w, d, -1, 2));
    };
    fenceRun(-6, 24, 30, 24);
    fenceRun(30, 24, 30, 2);

    // Hay bales.
    const hay = stdMat(0xd9b56b, 0.95);
    const hayGeo = new THREE.CylinderGeometry(0.9, 0.9, 1.4, 14);
    hayGeo.rotateZ(Math.PI / 2);
    for (const [x, z, ry] of [
      [6, 18, 0.4],
      [8.2, 18.6, 1.2],
      [7, 16, 2.3],
      [-8, -20, 0.9],
      [-5.6, -20.8, 0.1],
    ] as const) {
      const bale = new THREE.Mesh(hayGeo, hay);
      bale.position.set(x, height(x, z) + 0.9, z);
      bale.rotation.y = ry;
      bale.castShadow = true;
      scene.add(bale);
      const cap = new THREE.Mesh(
        new THREE.CylinderGeometry(0.92, 0.92, 0.5, 14),
        stdMat(0xe9eef6, 0.95),
      );
      cap.geometry.rotateZ(Math.PI / 2);
      cap.position.set(x, height(x, z) + 1.35, z);
      cap.rotation.y = ry;
      scene.add(cap);
      colliders.push(collider(x, z, 1.8, 1.8, 0, height(x, z) + 1.8));
    }

    return {
      scene,
      spawn: { pos: new THREE.Vector3(2, height(2, 2), 2), yaw: 1.84 },
      paintables,
      colliders,
      groundHeight: height,
      groundSurfaceIdAt: (x, z) => (pondDist(x, z) < POND.r - 0.5 ? 'pond' : 'ground'),
      bounds: { minX: -68, maxX: 68, minZ: -68, maxZ: 68 },
      baseFogDensity: 0.0052,
      dispose() {
        disposeScene(scene);
      },
    };
  },
};
